import { useEffect, useState } from 'react';
import { StakedCarouselProps } from './props';
import { wrap } from './utils';
import { Button, Container } from './styles';
import { AnimatePresence, motion } from 'framer-motion';
import {
  carouselPrimaryCard,
  carouselSecondaryCard,
  carouselTertiaryCard,
  carouselTransition,
} from './variants';
import { ErrorCard } from '@starsoft/common/components';

export default function StakedCarousel<T>({
  component,
  data,
  withoutTip = false,
  error,
  refetch,
}: StakedCarouselProps<T>) {
  const [page, setPage] = useState(0);
  const [lastPageChange, setLastPageChange] = useState<number>(
    new Date().getTime(),
  );
  const currentIndex = wrap(0, data?.length ? data?.length - 1 : 0, page);
  const nextIndex1 = wrap(0, data?.length ? data?.length - 1 : 0, page + 1);
  const nextIndex2 = wrap(0, data?.length ? data?.length - 1 : 0, page + 2);

  const currentItem = data?.[currentIndex];
  const nextItem1 = data?.[nextIndex1];
  const nextItem2 = data?.[nextIndex2];

  function paginate() {
    setPage(prev => prev + 1);
    setLastPageChange(new Date().getTime());
  }

  function moveCarousel() {
    const interval = setInterval(() => {
      if (new Date().getTime() - lastPageChange < 5000) {
        return;
      }

      setPage(prev => prev + 1);
    }, 5000);

    return () => clearInterval(interval);
  }

  useEffect(moveCarousel, [lastPageChange]);

  if (data?.length === 0) {
    return null;
  }

  if (error) {
    return (
      <Container>
        <ErrorCard error={error} refetch={refetch} />
      </Container>
    );
  }

  return (
    <Container>
      <Button onClick={paginate}>
        <i className="fas fa-chevron-right" />
      </Button>
      <AnimatePresence mode="sync">
        <motion.div
          className="card"
          initial="open"
          animate="open"
          exit="exit"
          variants={carouselPrimaryCard}
          transition={carouselTransition}
          transformTemplate={(_, generated) => `translateY(-50%) ${generated}`}
          key={`staked-carousel-${page}`}
        >
          {component(currentItem)}
        </motion.div>
        <motion.div
          className="card card-next-1"
          initial="open"
          animate="open"
          exit="exit"
          variants={withoutTip ? carouselPrimaryCard : carouselSecondaryCard}
          transition={{
            ...carouselTransition,
            delay: 0.1,
          }}
          transformTemplate={(_, generated) => `translateY(-50%) ${generated}`}
          key={`staked-carousel-${page + 1}`}
        >
          {component(nextItem1)}
        </motion.div>
        <motion.div
          className="card card-next-2"
          initial="open"
          animate="open"
          exit="exit"
          variants={withoutTip ? carouselPrimaryCard : carouselTertiaryCard}
          transition={{
            ...carouselTransition,
            delay: 0.15,
          }}
          transformTemplate={(_, generated) => `translateY(-50%) ${generated}`}
          key={`staked-carousel-${page + 2}`}
        >
          {component(nextItem2)}
        </motion.div>
      </AnimatePresence>
    </Container>
  );
}
